import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/api';
import { useSeo } from '../lib/seo';

const SP = "M12 1c.35 5.8 4.4 9.85 11 11-6.6 1.15-10.65 5.2-11 11-.35-5.8-4.4-9.85-11-11C7.6 10.85 11.65 6.8 12 1z";
const Spark = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style}><path d={SP} /></svg>
);

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useSeo({ title: 'Login | The T Touch', description: 'Sign in to The T Touch.', noindex: true });

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await login(email.trim(), password);
      nav(user.role === 'cleaner' ? '/staff' : user.role === 'client' ? '/portal' : '/owner', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden"
      style={{ background: 'radial-gradient(130% 120% at 50% -10%, #fbf8f1 0%, #f4f0e7 55%, #ece6d8 100%)' }}>
      {/* faint sparkles */}
      <Spark className="absolute" style={{ width: 120, height: 120, top: '8%', left: '10%', fill: '#16140e', opacity: 0.05, transform: 'rotate(-8deg)' }} />
      <Spark className="absolute" style={{ width: 74, height: 74, bottom: '14%', left: '18%', fill: '#16140e', opacity: 0.045 }} />
      <Spark className="absolute" style={{ width: 150, height: 150, top: -30, right: '8%', fill: '#16140e', opacity: 0.04 }} />
      <Spark className="absolute" style={{ width: 60, height: 60, bottom: '22%', right: '16%', fill: '#16140e', opacity: 0.06 }} />
      {/* line-art band */}
      <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ height: 96, background: "url('/assets/band.png') center bottom / auto 150px repeat-x", opacity: 0.06 }} />

      <div className="relative z-10 w-full text-center" style={{ maxWidth: 412 }}>
        {/* Big centred logo */}
        <Link to="/" aria-label="The T Touch home" className="inline-block">
          <img src="/assets/logo-mark.png" alt="The T Touch" className="mx-auto w-auto" style={{ height: 220, maxWidth: 'none' }} />
        </Link>
        <div className="inline-flex items-center gap-2 text-[0.82rem] font-semibold text-[var(--ttouch-muted)] mb-7 mt-3">
          <Spark className="w-[13px] h-[13px]" style={{ fill: '#a8782a' }} /> Premium home cleaning · Moreton Bay
        </div>

        {/* Card */}
        <div className="text-left bg-[#fffefb] border border-[var(--ttouch-line)] p-8 sm:p-9" style={{ borderRadius: 28, boxShadow: '0 2px 4px rgba(22,20,14,.04), 0 30px 60px -34px rgba(22,20,14,.4)' }}>
          <div className="flex items-center gap-2.5"><h1 className="text-[1.7rem] font-extrabold tracking-tight">Welcome back</h1><Spark className="w-[19px] h-[19px]" style={{ fill: '#a8782a' }} /></div>
          <p className="text-[0.92rem] text-[var(--ttouch-muted)] mt-1 mb-6">Lovely to see you — sign in to your T Touch.</p>

          <form onSubmit={submit}>
            <div className="mb-4">
              <label className="text-[0.82rem] font-bold block mb-2">Email</label>
              <div className="flex items-center gap-2.5 bg-[var(--ttouch-paper)] border-[1.5px] border-[var(--ttouch-line2)] rounded-[13px] px-4 py-3.5 focus-within:border-[var(--ttouch-ink)] focus-within:bg-white transition-colors">
                <svg viewBox="0 0 24 24" className="w-[17px] h-[17px] flex-none" fill="none" stroke="var(--ttouch-soft)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
                <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@thettouch.com.au" className="border-none bg-transparent outline-none w-full text-[0.95rem] placeholder:text-[var(--ttouch-soft)]" />
              </div>
            </div>
            <div className="mb-1">
              <label className="text-[0.82rem] font-bold block mb-2">Password</label>
              <div className="flex items-center gap-2.5 bg-[var(--ttouch-paper)] border-[1.5px] border-[var(--ttouch-line2)] rounded-[13px] px-4 py-3.5 focus-within:border-[var(--ttouch-ink)] focus-within:bg-white transition-colors">
                <svg viewBox="0 0 24 24" className="w-[17px] h-[17px] flex-none" fill="none" stroke="var(--ttouch-soft)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></svg>
                <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="border-none bg-transparent outline-none w-full text-[0.95rem] placeholder:text-[var(--ttouch-soft)]" />
              </div>
            </div>
            <div className="flex justify-end mt-1 mb-4"><span className="text-[0.82rem] text-[var(--ttouch-muted)] font-semibold cursor-default">Forgot password?</span></div>
            {error && <p data-testid="login-error" className="text-sm text-[#a8553f] font-medium mb-3">{error}</p>}
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2.5 bg-[#16140e] text-white rounded-[14px] py-4 font-bold text-base transition-transform hover:-translate-y-0.5 disabled:opacity-60">
              {loading ? 'Signing in…' : <>Sign in <svg viewBox="0 0 24 24" className="w-[17px] h-[17px]" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg></>}
            </button>
          </form>
        </div>

        <p className="text-[0.82rem] text-[var(--ttouch-muted)] mt-6 leading-relaxed">Owner, cleaners and clients all sign in here.<br /><b className="text-[var(--ttouch-ink)] font-semibold">Need access?</b> Just ask Taleeia to set you up.</p>
        <p className="text-[0.78rem] text-[var(--ttouch-soft)] mt-6">Taleeia Jackson · 0466 955 793 · thettouch.com.au</p>
      </div>
    </div>
  );
}
